/**
 * 語言切換
 *
 * Changing the reading locale is a state change, so it travels as a POST to
 * /locale and the server decides where to land the visitor.
 *
 * This file used to hold the opposite arrangement: a list of locales, a list of
 * reserved system paths, a list of static pages and a whitelist of path shapes,
 * all shipped from the backend on every page so the browser could work out for
 * itself whether the current page had a locale URL. It got every multi-segment
 * page wrong — team course, category and teacher pages, /cases/:uuid and
 * /speak/:language/:topic all had localized routes and all fell back to
 * ?locale=. The route table is on the server; asking it is the whole fix.
 */

/**
 * What the browser is about to submit. Split out from the DOM work because the
 * endpoint and the field names are the part that has to agree with Rails
 * routing and LocalesController; building the form element does not.
 *
 * @param {string} locale
 * @param {{location: {pathname: string, search: string, hash: string}, csrfToken: ?string}} context
 */
export function localeSubmission(locale, { location, csrfToken }) {
  return {
    action: '/locale',
    method: 'post',
    fields: {
      locale,
      return_path: `${location.pathname}${location.search}${location.hash}`,
      authenticity_token: csrfToken || ''
    }
  }
}

function readCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

function submit({ action, method, fields }) {
  const form = document.createElement('form');
  form.method = method;
  form.action = action;
  form.style.display = 'none';

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

/**
 * 切換頁面語言。
 *
 * 寫入偏好的唯一入口。伺服器會把訪客帶到同一頁的目標語系版本（如果那個 URL
 * 存在），否則原地回來並以新語系渲染。
 *
 * @param {string} locale
 */
export function switchLocale(locale) {
  submit(localeSubmission(locale, { location: window.location, csrfToken: readCsrfToken() }));
}

/**
 * 初始化語言選擇器
 * 為所有具有 data-language-switcher 屬性的 select 元素綁定事件
 */
export function initializeLanguageSwitchers() {
  document.addEventListener('DOMContentLoaded', function() {
    const switchers = document.querySelectorAll('[data-language-switcher]');

    switchers.forEach(switcher => {
      switcher.addEventListener('change', function() {
        switchLocale(this.value);
      });
    });
  });
}

// 自動初始化（如果是在瀏覽器環境中）
if (typeof window !== 'undefined') {
  initializeLanguageSwitchers();
}
